import { Link } from 'react-router';
import { ticketStatusLabels } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import type { BoardCard, TicketBoard as TicketBoardData } from '../types';
import { DeadlineDot } from '../ui/DeadlineDot';
import { TicketAvatar } from '../ui/TicketAvatar';
import { TicketPill } from '../ui/TicketPill';
import styles from './board.module.css';

const deadlineTextClass: Record<string, string | undefined> = {
  warning: styles.deadlineWarning,
  danger: styles.deadlineDanger,
  neutral: styles.deadlineNeutral,
};

/**
 * Backoffice 03: five columns (Neu · In Arbeit · Wartet auf Kunde · Gelöst · Geschlossen). The column head count
 * is the API's (cards may be fewer, A6); „Geschlossen" is the dashed tile. Cards open the record; status changes
 * happen there (no drag and drop).
 */
export function TicketBoard({ board, dimmed }: { board: TicketBoardData; dimmed: boolean }) {
  return (
    <div className={clsx(styles.board, dimmed && styles.dimmed)}>
      {board.columns.map((column) => {
        const cards = board.cards.filter((card) => card.status === column.key);
        return (
          <section key={column.key} className={styles.column} aria-label={column.label}>
            <div className={styles.columnHead}>
              <h2 className={styles.columnLabel}>{column.label}</h2>
              <span className={styles.columnCount}>{column.count}</span>
            </div>
            {cards.map((card) => (
              <TicketCard key={card.id} card={card} />
            ))}
          </section>
        );
      })}
      <section className={styles.column} aria-label={ticketStatusLabels.closed}>
        <div className={styles.columnHead}>
          <h2 className={styles.columnLabel}>{ticketStatusLabels.closed}</h2>
          <span className={styles.columnCount}>{board.closed.count}</span>
        </div>
        <div className={styles.closedTile}>{board.closed.label}</div>
      </section>
    </div>
  );
}

function TicketCard({ card }: { card: BoardCard }) {
  return (
    <Link
      to={`/tickets/${card.id}`}
      className={clsx(styles.card, card.critical && styles.cardCritical)}
      aria-label={`${card.numberLabel} ${card.title}`}
    >
      <div className={styles.cardTop}>
        <span className={styles.cardNumber}>{card.numberLabel}</span>
        {card.category ? (
          <TicketPill label={card.category} tone="neutral" className={styles.cardPill} />
        ) : null}
      </div>
      <div className={styles.cardTitle}>{card.title}</div>
      <div className={styles.cardLine}>{card.contactLine}</div>
      <div className={styles.cardFoot}>
        <DeadlineDot tone={card.deadline.tone} />
        <span className={clsx(styles.cardDeadline, deadlineTextClass[card.deadline.tone])}>
          {card.deadline.label}
        </span>
        <span className={styles.cardAssignee}>
          {card.assignee ? (
            <TicketAvatar initials={card.assignee.initials} name={card.assignee.name} size={22} tone="dark" />
          ) : (
            <span className={styles.unassigned}>unzugeordnet</span>
          )}
        </span>
      </div>
    </Link>
  );
}
